import React, { useMemo } from "react";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { Bar, Line } from "react-chartjs-2";
import Icon from "@mui/material/Icon";
import ChartDataLabels from "chartjs-plugin-datalabels";
import configs from "examples/Charts/LineCharts/ReportsLineChart/configs";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels // register plugin
);

const splitTimestampParts = (value = "") => {
  if (!value) return [null, null];
  const normalized = String(value).trim();
  if (!normalized) return [null, null];

  if (normalized.includes("\n")) {
    const [first, ...rest] = normalized
      .split("\n")
      .map((part) => part.trim())
      .filter(Boolean);
    return [first || null, rest.join(" ") || null];
  }

  const isoMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](.*))?$/);
  if (isoMatch) {
    return [isoMatch[1], isoMatch[2] ? isoMatch[2].replace(/Z$/, "").trim() || null : null];
  }
  const dmyMatch = normalized.match(/^(\d{2}-\d{2}-\d{4})(?:[T\s](.*))?$/);
  if (dmyMatch) {
    return [dmyMatch[1], dmyMatch[2] ? dmyMatch[2].replace(/Z$/, "").trim() || null : null];
  }

  const separators = [" | ", " - ", " � "];
  for (const separator of separators) {
    const index = normalized.indexOf(separator);
    if (index !== -1) {
      const first = normalized.slice(0, index).trim();
      const second = normalized.slice(index + separator.length).trim();
      return [first || normalized, second || null];
    }
  }

  const parts = normalized.split(/\s+/);
  if (parts.length > 1) {
    return [parts[0], parts.slice(1).join(" ") || null];
  }

  return [normalized, null];
};

function WaterChart({
  color,
  title,
  titleSx,
  chart,
  chartHeight,
  horizontal,
  timestamp,
  water_total,
  water_dailybird,
  water_avghour,
  totalLabel,
  showTotal,
}) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {} /*, yAxisMax*/);
  const barData = useMemo(
    () => ({
      ...data,
      datasets: (data?.datasets || []).map((dataset) => ({
        ...dataset,
        type: "bar",
        backgroundColor: dataset.backgroundColor || dataset.borderColor,
      })),
    }),
    [data]
  );
  const chartOptions = useMemo(
    () => (horizontal ? { ...options, indexAxis: "y" } : options),
    [horizontal, options]
  );
  const [timestampDate, timestampTime] = splitTimestampParts(timestamp);
  const usageSeries = Array.isArray(chart?.datasets?.[0]?.data) ? chart.datasets[0].data : null;
  const numericTotal = Number(water_total);
  const totalUsageValue = Number.isFinite(numericTotal)
    ? numericTotal
    : Array.isArray(usageSeries)
    ? usageSeries.reduce((sum, value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? sum + numeric : sum;
      }, 0)
    : null;
  const totalUsageDisplay = totalUsageValue != null ? totalUsageValue : "-";
  return (
    <Card sx={{ height: "100%" }}>
      <MDBox padding="1rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              mt={-5}
              height={chartHeight}
            >
              {horizontal ? (
                <Bar data={barData} options={chartOptions} redraw />
              ) : (
                <Line data={data} options={chartOptions} redraw />
              )}
            </MDBox>
          ),
          [chart, color, horizontal, barData, data, chartOptions]
        )}
        <MDBox pt={3} pb={1} px={1}>
          <MDBox
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "column" },
              alignItems: { xs: "flex-start", sm: "flex-start" },
              justifyContent: { xs: "space-between", sm: "flex-start" },
              gap: { xs: 1, sm: 0.5 },
              width: "100%",
            }}
          >
            <MDBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: { xs: 0.25, sm: 0.25 },
              }}
            >
              <MDTypography variant="h6" textTransform="capitalize" sx={titleSx}>
                {title}
              </MDTypography>
              {showTotal ? (
                <MDTypography variant="h6" textTransform="capitalize">
                  {totalLabel}: {totalUsageDisplay}L
                </MDTypography>
              ) : null}
            </MDBox>
            {timestamp ? (
              <>
                <MDBox
                  sx={{
                    display: { xs: "flex", sm: "none" },
                    flexDirection: "column",
                    alignItems: "flex-end",
                    fontSize: "0.95rem",
                    color: "text.secondary",
                    textAlign: "right",
                    ml: "auto",
                  }}
                >
                  <MDTypography variant="button" color="text">
                    {timestampDate || timestamp}
                  </MDTypography>
                  {timestampTime ? (
                    <MDTypography variant="button" color="text" fontWeight="light" sx={{ mt: 0.1 }}>
                      {timestampTime}
                    </MDTypography>
                  ) : null}
                </MDBox>
              </>
            ) : null}
          </MDBox>

          {timestamp ? (
            <MDBox
              sx={{
                position: { xs: "static", sm: "absolute" },
                bottom: { sm: 8 },
                left: { sm: 16 },
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 0.5,
                mt: { xs: 1.5, sm: 0 },
              }}
            >
              <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15, mr: 0.5 }}>
                <Icon>schedule</Icon>
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="light">
                {timestamp}
              </MDTypography>
            </MDBox>
          ) : null}
        </MDBox>
      </MDBox>
    </Card>
  );
}

WaterChart.defaultProps = {
  color: "info",
  description: "",
  canvasHeight: "12.5rem",
  totalLabel: "Total Usage",
  horizontal: false,
  showTotal: true,
  titleSx: {},
};

// Typechecking props for the ReportsLineChart
WaterChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  chartHeight: PropTypes.string,
  horizontal: PropTypes.bool,
  water_total: PropTypes.number,
  water_dailybird: PropTypes.number,
  water_avghour: PropTypes.number,
  timestamp: PropTypes.string,
  totalLabel: PropTypes.string,
  showTotal: PropTypes.bool,
  titleSx: PropTypes.object,
};

export default WaterChart;
